import React, { useEffect, useRef, useState } from 'react';
import {
  isDiwaliCampaignActive,
  markDiwaliFireworksPlayed,
  prefersReducedMotion,
  wasDiwaliFireworksPlayed,
} from '../data/diwaliCampaign';

const COLORS = ['#F5B800', '#FFE08A', '#FF9A1F', '#E23B2F'];
const DURATION_MS = 4000;
const LAUNCHES_MS = [120, 380, 640, 920];

const DiwaliFireworks = () => {
  const canvasRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!isDiwaliCampaignActive() || prefersReducedMotion() || wasDiwaliFireworksPlayed()) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      if (wasDiwaliFireworksPlayed()) return;
      setPlaying(true);
    }, 60);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return undefined;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.scale(ratio, ratio);

    const rockets = [];
    const sparks = [];
    const started = performance.now();
    let launched = 0;
    let frame = 0;

    const burst = (x, y) => {
      const count = 18;
      for (let index = 0; index < count; index += 1) {
        const angle = (Math.PI * 2 * index) / count;
        const speed = 0.35 + Math.random() * 0.55;
        sparks.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          decay: 0.011 + Math.random() * 0.01,
          color: Math.random() < 0.14 ? COLORS[3] : COLORS[index % 3],
          size: 2.2 + Math.random() * 1.4,
        });
      }
    };

    const tick = (now) => {
      const elapsed = now - started;
      while (launched < LAUNCHES_MS.length && elapsed >= LAUNCHES_MS[launched]) {
        rockets.push({
          x: 6 + Math.random() * (width * 0.28),
          y: height - 3,
          vx: 0.28 + Math.random() * 0.5,
          vy: -2.15 - Math.random() * 0.55,
        });
        launched += 1;
      }

      context.clearRect(0, 0, width, height);

      for (let index = rockets.length - 1; index >= 0; index -= 1) {
        const rocket = rockets[index];
        rocket.x += rocket.vx;
        rocket.y += rocket.vy;
        rocket.vy += 0.018;
        context.globalAlpha = 0.95;
        context.fillStyle = COLORS[0];
        context.beginPath();
        context.arc(rocket.x, rocket.y, 1.7, 0, Math.PI * 2);
        context.fill();
        if (rocket.vy > -0.4 || rocket.y < height * 0.22) {
          burst(rocket.x, rocket.y);
          rockets.splice(index, 1);
        }
      }

      for (let index = sparks.length - 1; index >= 0; index -= 1) {
        const spark = sparks[index];
        spark.x += spark.vx;
        spark.y += spark.vy;
        spark.vy += 0.012;
        spark.life -= spark.decay;
        if (spark.life <= 0) {
          sparks.splice(index, 1);
          continue;
        }
        context.globalAlpha = spark.life;
        context.fillStyle = spark.color;
        context.fillRect(spark.x, spark.y, spark.size, spark.size);
      }

      context.globalAlpha = 1;
      if (elapsed < DURATION_MS) {
        frame = window.requestAnimationFrame(tick);
      } else {
        markDiwaliFireworksPlayed();
        setPlaying(false);
      }
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [playing]);

  if (!playing) return null;

  return <canvas ref={canvasRef} className="diwali-fireworks" width="180" height="140" aria-hidden="true" />;
};

export default DiwaliFireworks;
